import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Check, Sparkles, Zap } from "lucide-react";
import { Button, Sticker, toastManager } from "../components/ui";
import { SettingsHeader } from "../components/SettingsHeader";
import { useSubscription } from "../lib/useSubscription";
import { startCheckout, openPortal, startTopup } from "../lib/billing";
import { rememberBillingState } from "../lib/billingSync";
import type { FreeQuotaState } from "../lib/billing";
import { cn } from "@/lib/utils";
import { formatPrice, planName, pricing } from "../lib/pricing";
import { track } from "../lib/analytics";
import type { PlanInterval, PlanTier } from "../lib/pricing";

export const Route = createFileRoute("/settings/billing")({
  component: BillingSettings,
});

const PLAN_TONES: Record<PlanTier, string> = {
  creator: "face-sky edge-sky-edge",
  studio: "face-lilac-300 edge-lilac-600",
};

function BillingSettings() {
  const [busy, setBusy] = useState<string | null>(null);
  const sub = useSubscription();

  async function go(key: string, getUrl: () => Promise<string>, fail: string) {
    setBusy(key);
    try {
      const url = await getUrl();
      rememberBillingState(sub.data);
      window.location.assign(url);
    } catch (e) {
      setBusy(null);
      toastManager.add({ type: "error", title: fail });
      console.error(e);
    }
  }

  const status = sub.data?.status;
  const isActive = status === "active";
  // Plans are offered to anyone without a live subscription; a past-due one is
  // fixed through the billing portal, not a second checkout.
  const showPlans =
    sub.data !== undefined && !isActive && status !== "past_due";
  const tierName = planName(sub.data?.tier);
  const credits = sub.data?.credits;
  const canManageBilling =
    isActive || status === "past_due" || sub.data?.hasBillingPortal === true;
  const renewsAt = formatDate(sub.data?.renewsAt);

  return (
    <div>
      <SettingsHeader title="Plan & billing">
        Your balance, your plan and credit top-ups.
      </SettingsHeader>
      <div className="grid gap-4 sm:gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
        {/* BALANCE */}
        <section className="tactile p-4 sm:p-6">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-xl text-ink">Your balance</h3>
            <Sticker tone={isActive ? "lilac" : "neutral"}>
              {isActive
                ? `✦ ${tierName}`
                : status === "past_due"
                  ? "Past due"
                  : "Free plan"}
            </Sticker>
          </div>

          <div className="mt-5 flex items-end gap-2">
            <Zap
              className="mb-2 size-8 fill-sun text-sun-edge"
              aria-hidden="true"
            />
            <span className="font-display text-5xl font-semibold leading-none text-ink sm:text-6xl">
              {sub.isLoading ? "…" : (sub.data?.credits.balance ?? 0)}
            </span>
            <span className="mb-1 text-sm font-bold text-ink-soft">
              credits
              {sub.data?.credits.monthlyAllowance
                ? ` · ${sub.data.credits.monthlyAllowance} each month`
                : ""}
            </span>
          </div>

          {status === "free" && sub.data && (
            <div className="mt-6 space-y-3">
              <p className="text-xs font-extrabold uppercase tracking-wider text-ink-faint">
                Free allowance
              </p>
              <QuotaMeter
                label="Character drafts"
                quota={sub.data.freeQuota.iterations}
              />
              <QuotaMeter
                label="Pack variations"
                quota={sub.data.freeQuota.variations}
              />
              {sub.data.freeQuota.locks && (
                <QuotaMeter label="Lock-ins" quota={sub.data.freeQuota.locks} />
              )}
              <p className="text-xs font-semibold text-ink-soft">
                Free mascots are watermarked previews: subscribe to download
                them.
              </p>
            </div>
          )}

          {isActive && credits && (
            <dl className="mt-5 space-y-2 text-sm font-semibold">
              <CreditRow
                label="Plan credits"
                amount={credits.subscription ?? 0}
                expiresAt={credits.subscriptionExpiresAt}
              />
              {(credits.purchased ?? 0) > 0 && (
                <CreditRow
                  label="Top-up credits"
                  amount={credits.purchased ?? 0}
                  expiresAt={credits.purchasedExpiresAt}
                  first
                />
              )}
            </dl>
          )}

          {isActive && (
            <p className="mt-5 text-sm font-semibold text-ink-soft">
              {sub.data?.cancelAtPeriodEnd
                ? `${tierName} ends ${renewsAt ?? "at the end of the period"}.`
                : sub.data?.interval === "year"
                  ? `${tierName} yearly renews ${renewsAt ?? "every year"}; credits arrive every month.`
                  : `${tierName} monthly renews ${renewsAt ?? "every month"}.`}
            </p>
          )}

          {canManageBilling && (
            <div className="mt-6 border-t-2 border-line pt-5">
              <Button
                variant="outline"
                loading={busy === "portal"}
                onClick={() =>
                  void go("portal", openPortal, "Could not open billing portal")
                }
              >
                Manage billing
              </Button>
              <p className="mt-2 text-xs font-semibold text-ink-soft">
                {isActive
                  ? "Change your plan, update your card, see invoices, or cancel your subscription."
                  : "See invoices and payment methods."}
              </p>
            </div>
          )}
        </section>

        <section className="flex flex-col gap-4 sm:gap-6">
          {showPlans && (
            <PlanPicker
              busy={busy}
              onChoose={(tier, interval) => {
                const plan = pricing.plans.find((p) => p.tier === tier);
                track("begin_checkout", {
                  currency: pricing.currency,
                  value:
                    (interval === "year" ? plan?.annual : plan?.monthly) ?? 0,
                  item_id: `${tier}_${interval}`,
                });
                void go(
                  `plan-${tier}`,
                  () => startCheckout(tier, interval),
                  "Could not start checkout"
                );
              }}
            />
          )}
          {isActive && (
            <TopupCard
              busy={busy === "topup"}
              disabled={busy !== null}
              onBuy={() => {
                track("begin_checkout", {
                  currency: pricing.currency,
                  value: pricing.topup.price,
                  item_id: "topup",
                });
                void go("topup", startTopup, "Could not start checkout");
              }}
            />
          )}
          <CreditRules />
        </section>
      </div>
    </div>
  );
}

function QuotaMeter({
  label,
  quota,
}: {
  label: string;
  quota: FreeQuotaState;
}) {
  const pct = quota.limit ? (quota.remaining / quota.limit) * 100 : 0;
  return (
    <div>
      <div className="mb-1 flex justify-between text-sm font-bold text-ink">
        <span>{label}</span>
        <span className="text-ink-soft">
          {quota.remaining} of {quota.limit} left
        </span>
      </div>
      <div className="h-3.5 overflow-hidden rounded-full border-2 border-line-strong bg-paper">
        <div
          className="h-full rounded-full bg-mint transition-[width]"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function formatDate(iso: string | null | undefined): string | null {
  return iso ? new Date(iso).toLocaleDateString() : null;
}

function CreditRow({
  label,
  amount,
  expiresAt,
  first = false,
}: {
  label: string;
  amount: number;
  expiresAt: string | null | undefined;
  /** Top-ups expire one by one: the date is when the first of them does. */
  first?: boolean;
}) {
  const date = formatDate(expiresAt);
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink-soft">
        {label}
        {amount > 0 && date && (
          <span className="block text-xs text-ink-faint">
            {first ? "First ones expire" : "Expire"} {date}
          </span>
        )}
      </dt>
      <dd className="font-display text-lg font-semibold text-ink">{amount}</dd>
    </div>
  );
}

function PlanPicker({
  busy,
  onChoose,
}: {
  busy: string | null;
  onChoose: (tier: PlanTier, interval: PlanInterval) => void;
}) {
  const [interval, setBillingInterval] = useState<PlanInterval>("month");
  return (
    <div className="tactile p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-xl text-ink">Choose a plan</h3>
          <p className="mt-1 text-xs font-semibold text-ink-soft">
            Every plan unlocks downloads: clean SVG, PNG and WebP with no
            watermark, including the mascots you've already made.
          </p>
        </div>
        <div
          role="group"
          aria-label="Billing period"
          className="inline-flex rounded-full border-2 border-line-strong bg-paper p-1"
        >
          {(
            [
              ["month", "Monthly"],
              ["year", "Yearly · 2 months free"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={interval === value}
              onClick={() => setBillingInterval(value)}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-extrabold",
                interval === value
                  ? "bg-ink text-white"
                  : "text-ink-soft hover:text-ink"
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {pricing.plans.map((plan) => {
          const key = `plan-${plan.tier}`;
          return (
            <div
              key={plan.tier}
              className={cn(
                "relative flex flex-col overflow-hidden raised depth-4 rounded-3xl p-5 text-ink",
                PLAN_TONES[plan.tier]
              )}
            >
              {plan.tier === "studio" && (
                <Sparkles
                  className="absolute -right-3 -top-3 size-20 rotate-12 text-lilac-200"
                  aria-hidden="true"
                />
              )}
              <h4 className="relative text-lg text-ink">{plan.name}</h4>
              <p className="relative mt-1 font-display text-3xl font-semibold">
                {formatPrice(interval === "year" ? plan.annual : plan.monthly)}
                <span className="text-sm font-bold text-ink/70">
                  /{interval}
                </span>
              </p>
              <p className="relative mt-2 flex items-center gap-1.5 text-sm font-bold text-ink/80">
                <Check className="size-4 shrink-0" aria-hidden="true" />
                {plan.credits} credits every month
              </p>
              <p className="relative mt-1 flex items-center gap-1.5 text-sm font-bold text-ink/80">
                <Check className="size-4 shrink-0" aria-hidden="true" />
                Downloads in SVG, PNG &amp; WebP
              </p>
              <Button
                variant="dark"
                className="relative mt-4"
                loading={busy === key}
                disabled={busy !== null && busy !== key}
                onClick={() => onChoose(plan.tier, interval)}
              >
                Choose {plan.name}
              </Button>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-xs font-semibold text-ink-faint">
        Prices in {pricing.currency}. Any sales tax or VAT is shown at checkout.
        Change or cancel your plan any time from this page.
      </p>
    </div>
  );
}

function TopupCard({
  busy,
  disabled,
  onBuy,
}: {
  busy: boolean;
  disabled: boolean;
  onBuy: () => void;
}) {
  return (
    <div className="tactile flex flex-col gap-4 p-4 sm:p-6 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h3 className="text-xl text-ink">Need more this month?</h3>
        <p className="mt-1 text-sm font-semibold text-ink-soft">
          Top up with {pricing.topup.credits} credits for{" "}
          {formatPrice(pricing.topup.price)}. They are added once the payment
          clears and last one month.
        </p>
      </div>
      <Button
        variant="outline"
        className="shrink-0"
        loading={busy}
        disabled={disabled && !busy}
        onClick={onBuy}
      >
        <Zap /> Buy {pricing.topup.credits} credits
      </Button>
    </div>
  );
}

function CreditRules() {
  return (
    <div className="rounded-2xl border-2 border-dashed border-line-strong p-4 sm:p-5">
      <h3 className="text-base text-ink">How credits work</h3>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm font-semibold text-ink-soft">
        <li>
          Plan credits arrive every month, on monthly and yearly plans alike.
          Unused ones expire when the month ends; they do not roll over.
        </li>
        <li>
          Top-ups are for subscribers and expire one month after purchase.
        </li>
        <li>Credits that expire soonest are used first.</li>
        <li>
          Upgrading takes effect at once: you pay the difference for the rest of
          the period and get the extra credits for it. Downgrading removes them
          the same way.
        </li>
        <li>
          Cancelling keeps your plan and credits until the period you paid for
          ends.
        </li>
      </ul>
    </div>
  );
}
